import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, ArrowRight, TrendingUp, DollarSign, Building2, Globe, CheckCircle, Loader2, Briefcase } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvestorPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "", email: "", company: "", title: "", phone: "",
    investor_type: "", investment_range: "", sectors: [],
    geographic_focus: "", portfolio_size: "", linkedin_url: "",
    investment_thesis: "", how_heard: "", message: ""
  });

  const sectors = ["Climate Tech", "FinTech / RegTech", "Cross-Border Services", "HealthTech", "EdTech", "PropTech", "Other"];
  const investorTypes = ["Angel Investor", "Venture Capital", "Family Office", "Corporate VC", "Institutional Investor", "Other"];
  const investmentRanges = ["$10K - $50K", "$50K - $250K", "$250K - $1M", "$1M - $5M", "$5M+"];

  const handleSectorToggle = (sector) => {
    setFormData(prev => ({
      ...prev,
      sectors: prev.sectors.includes(sector) 
        ? prev.sectors.filter(s => s !== sector)
        : [...prev.sectors, sector]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.investor_type) {
      toast({ title: "Required fields missing", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/investors`, formData);
      setSubmitted(true);
      toast({ title: "Registration Successful!", description: "We'll be in touch shortly" });
    } catch (error) {
      toast({ title: "Error", description: "Please try again", variant: "destructive" });
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-6">
        <Card className="max-w-lg bg-gray-800/50 border-gray-700 text-center">
          <CardContent className="pt-12 pb-8">
            <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Welcome to SKI AXIS Investor Network!</h2>
            <p className="text-gray-400 mb-6">Thank you for your interest. Our team will review your profile and contact you within 48 hours.</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate("/governance")} className="bg-green-600 hover:bg-green-700">View Portfolio</Button>
              <Button onClick={() => navigate("/")} variant="outline" className="border-gray-600 text-gray-300">Home</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <header className="bg-gray-900/80 backdrop-blur-xl border-b border-gray-700 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate("/")} className="text-gray-300 hover:text-white hover:bg-gray-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Home
              </Button>
              <div className="h-8 w-px bg-gray-700"></div>
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-2 rounded-xl">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Investor Portal</h1>
                  <p className="text-sm text-gray-400">Join our investor network</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ArrowLeft className="h-5 w-5 text-green-400" />
              <span className="text-lg font-bold text-white">SKI AXIS</span>
              <ArrowRight className="h-5 w-5 text-green-400" />
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Stats Banner */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-green-400">45</p>
            <p className="text-gray-400 text-sm">Portfolio Startups</p>
          </div>
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-blue-400">8/10</p>
            <p className="text-gray-400 text-sm">Avg Governance</p>
          </div>
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-purple-400">20+</p>
            <p className="text-gray-400 text-sm">Jobs Created</p>
          </div>
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-orange-400">87%</p>
            <p className="text-gray-400 text-sm">Compliance Rate</p>
          </div>
        </div>

        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white text-2xl">Investor Registration</CardTitle>
            <CardDescription className="text-gray-400">Join our network to access deal flow from governance-ready startups</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Full Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="bg-gray-900 border-gray-600 text-white" placeholder="John Smith" />
                </div>
                <div>
                  <Label className="text-gray-300">Email *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="bg-gray-900 border-gray-600 text-white" placeholder="john@example.com" />
                </div>
                <div>
                  <Label className="text-gray-300">Company/Fund</Label>
                  <Input value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="bg-gray-900 border-gray-600 text-white" placeholder="ABC Ventures" />
                </div>
                <div>
                  <Label className="text-gray-300">Title</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="bg-gray-900 border-gray-600 text-white" placeholder="Managing Partner" />
                </div>
                <div>
                  <Label className="text-gray-300">Phone</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="bg-gray-900 border-gray-600 text-white" placeholder="+971 50 123 4567" />
                </div>
                <div>
                  <Label className="text-gray-300">LinkedIn Profile</Label>
                  <Input value={formData.linkedin_url} onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})} className="bg-gray-900 border-gray-600 text-white" placeholder="linkedin.com/in/..." />
                </div>
              </div>

              {/* Investment Profile */}
              <div className="border-t border-gray-700 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Briefcase className="h-5 w-5 text-blue-500" />Investment Profile</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Investor Type *</Label>
                    <Select value={formData.investor_type} onValueChange={(v) => setFormData({...formData, investor_type: v})}>
                      <SelectTrigger className="bg-gray-900 border-gray-600 text-white"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {investorTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Typical Investment Size</Label>
                    <Select value={formData.investment_range} onValueChange={(v) => setFormData({...formData, investment_range: v})}>
                      <SelectTrigger className="bg-gray-900 border-gray-600 text-white"><SelectValue placeholder="Select range" /></SelectTrigger>
                      <SelectContent>
                        {investmentRanges.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Geographic Focus</Label>
                    <Input value={formData.geographic_focus} onChange={(e) => setFormData({...formData, geographic_focus: e.target.value})} className="bg-gray-900 border-gray-600 text-white" placeholder="MENA, Asia, Global" />
                  </div>
                  <div>
                    <Label className="text-gray-300">Current Portfolio Size</Label>
                    <Input value={formData.portfolio_size} onChange={(e) => setFormData({...formData, portfolio_size: e.target.value})} className="bg-gray-900 border-gray-600 text-white" placeholder="e.g., 20 companies" />
                  </div>
                </div>
              </div>

              {/* Sector Interests */}
              <div className="border-t border-gray-700 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Globe className="h-5 w-5 text-green-500" />Sector Interests</h3>
                <div className="flex flex-wrap gap-3">
                  {sectors.map(sector => (
                    <div key={sector} onClick={() => handleSectorToggle(sector)} 
                      className={`px-4 py-2 rounded-lg cursor-pointer transition-all ${formData.sectors.includes(sector) ? 'bg-green-600 text-white' : 'bg-gray-900 text-gray-300 border border-gray-700 hover:border-green-500'}`}>
                      {sector}
                    </div>
                  ))}
                </div>
              </div>

              {/* Investment Thesis */}
              <div className="border-t border-gray-700 pt-6">
                <Label className="text-gray-300">Investment Thesis / What You Look For</Label>
                <Textarea value={formData.investment_thesis} onChange={(e) => setFormData({...formData, investment_thesis: e.target.value})} 
                  className="bg-gray-900 border-gray-600 text-white min-h-24" placeholder="Describe your investment focus, stage preferences, and what makes a startup attractive to you..." />
              </div>

              {/* Additional */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">How did you hear about us?</Label>
                  <Input value={formData.how_heard} onChange={(e) => setFormData({...formData, how_heard: e.target.value})} className="bg-gray-900 border-gray-600 text-white" placeholder="Referral, LinkedIn, Event..." />
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Additional Message</Label>
                <Textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} className="bg-gray-900 border-gray-600 text-white" placeholder="Any specific startups or deals you're interested in?" />
              </div>

              <Button type="submit" disabled={loading} className="w-full py-6 text-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Submitting...</> : <><TrendingUp className="h-5 w-5 mr-2" />Join Investor Network</>}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InvestorPage;
